import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { Observable, concatMap, forkJoin, from, of } from 'rxjs';
import {
  Company,
  CompanyVendor,
  ExemptVendor,
  Note,
  SignedDocument,
  Vendor
} from 'src/app/classes/models';
import { ViveProgress } from 'src/app/classes/models/vive-progress';
import { TicketUrl } from 'src/app/classes/ticket-url';
import { ClientPackageType } from 'src/app/interfaces/client-package-type';
import { ComplianceReviewAction } from 'src/app/interfaces/compliance-review-action';
import { ComplianceSteps } from 'src/app/interfaces/compliance-steps';
import { State } from 'src/app/interfaces/state';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { ViveStatuses } from 'src/app/interfaces/vive-statuses';
import { VsaBypassReason } from 'src/app/interfaces/vsa-bypass-reason';
import { ChooseExemptVendorTypeModalComponent } from 'src/app/modals/choose-exempt-vendor-type-modal/choose-exempt-vendor-type-modal.component';
import { ComplianceStatusModalComponent } from 'src/app/modals/compliance-status-modal/compliance-status-modal.component';
import { ConfirmArchiveVendorModalComponent } from 'src/app/modals/confirm-archive-vendor-modal/confirm-archive-vendor-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { CreateTicketModalComponent } from 'src/app/modals/create-ticket-modal/create-ticket-modal.component';
import { MarkVsaSignedModalComponent } from 'src/app/modals/mark-vsa-signed-modal/mark-vsa-signed-modal.component';
import { SelectComplianceProgramModalComponent } from 'src/app/modals/select-compliance-program-modal/select-compliance-program-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { ClientRegisteredVendorService } from 'src/app/services/client-registered-vendor.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ComplianceStepStatusService } from 'src/app/services/compliance-step-status.service';
import { FileService } from 'src/app/services/file.service';
import { InsuranceExpirationService } from 'src/app/services/insurance-expiration.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { SignedDocumentService } from 'src/app/services/signed-document.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { ApVendorUtilityService } from 'src/app/services/utility/ap-vendor-utility.service';
import { VendorService } from 'src/app/services/vendor.service';
import { ViveProgressService } from 'src/app/services/vive-progress.service';
import { companyVendorsService } from 'src/app/shared/vive-api/company-vendors.service';
import { signedDocumentsService } from 'src/app/shared/vive-api/signed-documents.service';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';
import { ViveApiError } from 'src/app/shared/vive-api/vive-api.error';

declare type ComplianceRoute =
  | 'business-review'
  | 'owner-review'
  | 'trade-review'
  | 'insurance-review'
  | 'final-review';
@Component({
  selector: 'app-compliance-review',
  templateUrl: './compliance-review.component.html',
  styleUrls: ['./compliance-review.component.less'],
  providers: [
    SharedDataService,
    ComplianceStepStatusService,
    InsuranceExpirationService
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceReviewComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private CompanyVendorService: CompanyVendorService,
    private VendorService: VendorService,
    private ToastService: ToastService,
    private SharedDataService: SharedDataService,
    private ViveProgressService: ViveProgressService,
    private router: Router,
    private ModalService: NgbModal,
    private AuthService: AuthService,
    private FileService: FileService,
    private UtilityService: UtilityService,
    private ClientRegisteredVendorService: ClientRegisteredVendorService,
    private ApVendorUtilityService: ApVendorUtilityService,
    private SignedDocumentService: SignedDocumentService,
    private ComplianceStepStatusService: ComplianceStepStatusService,
    private InsuranceExpirationService: InsuranceExpirationService,
    private cdr: ChangeDetectorRef
  ) {
    this.SharedDataService.listenForEvent<
      Pick<ViveProgress, 'type' | 'status'> | TicketUrl | Vendor
    >().subscribe({
      next: ({ data, eventName }) => {
        if (eventName === 'GetReviewAction') {
          this.SharedDataService.sendEvent('SetReviewAction', this.action);
          this.cdr.markForCheck();
        }

        if (
          eventName === 'reloadComplianceProgress' &&
          data instanceof ViveProgress
        ) {
          const { type } = data;

          if (!type) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing compliance progress type.',
              { returnError: true }
            );
          }

          this.loadComplianceProgress(type);
        }

        if (
          eventName === 'GetComplianceProgress' &&
          data instanceof ViveProgress
        ) {
          const { type } = data;

          if (!type) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Cannot set type missing Compliance Progress.',
              { returnError: true }
            );
          }

          const ComplianceProgress = this.ComplianceProgressMap.get(type);

          if (ComplianceProgress === undefined) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Compliance Progress not defined in map.',
              { returnError: true }
            );
          }

          this.CurrentComplianceProgress = ComplianceProgress;
          this.SharedDataService.sendEvent(
            'SetComplianceProgress',
            ComplianceProgress
          );
          this.cdr.markForCheck();
        }

        // If a component has updated it's own compliance progress (such as manually through the modal),
        // we can update the value here.
        if (
          eventName === 'SetUpdatedComplianceProgress' &&
          data instanceof ViveProgress
        ) {
          if (!data) {
            throw this.ToastService.addErrorToast(
              'Error',
              'There was an error setting Compliance Progress. Missing data.',
              { returnError: true }
            );
          }

          const { type, status } = data;

          if (!type) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Compliance Progress type.',
              { returnError: true }
            );
          }

          if (!status) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Compliance Progress status.',
              { returnError: true }
            );
          }

          this.handleUpdatedComplianceProgress(data);
          this.cdr.markForCheck();
        }

        if (
          eventName === 'UpdateComplianceProgress' &&
          data instanceof ViveProgress
        ) {
          if (!data) {
            throw this.ToastService.addErrorToast(
              'Error',
              'There was an error setting Compliance Progress. Missing data.',
              { returnError: true }
            );
          }

          const { type, status } = data;

          if (!type) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Compliance Progress type.',
              { returnError: true }
            );
          }

          if (!status) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Compliance Progress status.',
              { returnError: true }
            );
          }

          // Only set this if the Compliance Progress doesn't exist within the map.
          const NewComplianceProgress = {
            CompanyId: this.CompanyId,
            VendorId: this.VendorId,
            CompanyVendorId: this.CompanyVendorId
          };

          const ComplianceProgress: ViveProgress = Object.assign(
            this.ComplianceProgressMap.get(type) || NewComplianceProgress,
            data
          );

          this.ViveProgressService.saveViveProgress(
            ComplianceProgress
          ).subscribe({
            next: (ComplianceProgress) => {
              this.handleUpdatedComplianceProgress(ComplianceProgress);
              this.cdr.markForCheck();
            }
          });
        }

        if (eventName === 'GoToNextStep') {
          this.goToNextStep();
        }

        if (
          eventName === 'ReloadClientVendorDataOpenTicket' &&
          data instanceof TicketUrl
        ) {
          this.loadCompanyVendor().subscribe({
            complete: () => {
              const { url } = data;

              const CurrentComplianceProgress = this.ComplianceProgressMap.get(
                this.CurrentComplianceStep!
              );
              this.CurrentComplianceProgress =
                CurrentComplianceProgress || null;
              this.SharedDataService.sendEvent(
                'SetComplianceProgress',
                CurrentComplianceProgress
              );

              this.ToastService.addSuccessToast(
                'Success!',
                'Successfully created Zendesk Ticket.'
              );
              window.open(url, '_blank');
              this.cdr.markForCheck();
            }
          });
        }

        if (eventName === 'SetVendor' && data instanceof Vendor) {
          this.Vendor = data;
          if (this.CompanyVendor) {
            this.CompanyVendor = structuredClone(
              Object.assign(this.CompanyVendor, { Vendor: data })
            );
          }
          this.cdr.markForCheck();
        }

        if (
          eventName === 'UpdateCompanyVendor' &&
          data instanceof CompanyVendor
        ) {
          if (this.CompanyVendor) {
            Object.assign(this.CompanyVendor, data);
          }
          this.cdr.markForCheck();
        }
      }
    });
  }

  CompanyVendorId!: number;
  CompanyComplianceTypeId!: number;
  CompanyVendor?: CompanyVendor;
  Vendor?: Vendor;
  Company?: Company;
  action!: ComplianceReviewAction;
  clientPackageType!: ClientPackageType;

  CompanyId?: number;
  VendorId?: number;

  // Represents the current Compliance step progress we're processing.
  CurrentComplianceProgress: ViveProgress | null = null;

  ComplianceSteps: Set<ComplianceSteps> = new Set([
    'Vendor Info',
    'Owner Info',
    'Trades & Licenses',
    'Insurance'
  ]);

  CurrentComplianceStep?: ComplianceSteps;

  ComplianceProgressMap: Map<ComplianceSteps, ViveProgress | null> = new Map([
    ['Vendor Info', null],
    // Only add Owner Info if the Client is marked as 'FULL' package type.
    // ['Owner Info', null],
    ['Trades & Licenses', null],
    ['Insurance', null]
    // ['Final Review', null]
  ]);

  ComplianceStepToNextStep: {
    'Vendor Info': () => 'Owner Info' | 'Trades & Licenses';
    'Owner Info': () => 'Trades & Licenses';
    'Trades & Licenses': () => 'Insurance';
    Insurance: () => 'Final Review';
  } = {
    'Vendor Info': () =>
      this.clientPackageType === 'FULL' ? 'Owner Info' : 'Trades & Licenses',
    'Owner Info': () => 'Trades & Licenses',
    'Trades & Licenses': () => 'Insurance',
    Insurance: () => 'Final Review'
  };

  ComplianceStepToRouteMap: {
    'Vendor Info': 'business-review';
    'Owner Info': 'owner-review';
    'Trades & Licenses': 'trade-review';
    Insurance: 'insurance-review';
    'Final Review': 'final-review';
  } = {
    'Vendor Info': 'business-review',
    'Owner Info': 'owner-review',
    'Trades & Licenses': 'trade-review',
    Insurance: 'insurance-review',
    'Final Review': 'final-review'
  };

  ComplianceRouteToStepMap: {
    'business-review': 'Vendor Info';
    'owner-review': 'Owner Info';
    'trade-review': 'Trades & Licenses';
    'insurance-review': 'Insurance';
    'final-review': 'Final Review';
  } = {
    'business-review': 'Vendor Info',
    'owner-review': 'Owner Info',
    'trade-review': 'Trades & Licenses',
    'insurance-review': 'Insurance',
    'final-review': 'Final Review'
  };

  acceptableVendorConversionStatuses: Set<ViveStatuses> = new Set([
    'Registering',
    'Awaiting Payment'
  ]);
  canConvertVendorToClientRegisteredVendor: boolean = false;

  SharedOperationStates = new Set<State>();

  // VPDW-4205: askToApproveVendor() runs on load and again on every step's progress save,
  // and this component outlives step navigation — so this keeps the "why you were not
  // offered approval" toast to one per review session.
  hasWarnedApprovalBlocked: boolean = false;

  LoggedInUser = this.AuthService.getUser();

  handleUpdatedComplianceProgress(ComplianceProgress: ViveProgress) {
    const { type } = ComplianceProgress;

    if (!type) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Compliance progress is missing type.',
        { returnError: true }
      );
    }

    const ComplianceProgressInstance = new ViveProgress(ComplianceProgress);
    this.ComplianceProgressMap.set(type, ComplianceProgressInstance);
    this.CurrentComplianceProgress = ComplianceProgressInstance;
    this.SharedDataService.sendEvent(
      'SetComplianceProgressAfterUpdate',
      ComplianceProgressInstance
    );
    this.askToApproveVendor();
    this.cdr.markForCheck();
  }

  askToApproveVendor() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor information.',
        { returnError: true }
      );
    }

    const {
      id,
      CompanyId,
      VendorId,
      viveStatus,
      resignRequired,
      needsFeeWaiveRenewal,
      RecentPendingOrPaidCompanyVendorPayment
    } = this.CompanyVendor;

    const passesStatusAndPaymentGates = !!(
      viveStatus &&
      viveStatus !== 'Approved' &&
      new Set(['Reviewing', 'Renewing']).has(viveStatus) &&
      Array.from(this.ComplianceProgressMap.values()).every(
        (ComplianceProgress) => {
          return ComplianceProgress && ComplianceProgress.status === 'Passed';
        }
      ) &&
      RecentPendingOrPaidCompanyVendorPayment &&
      RecentPendingOrPaidCompanyVendorPayment.expStatus !== 'EXPIRED'
    );

    // VPDW-4205: reasons this vendor must not be offered one-click approval even though
    // every step card reads 'Passed'. Each one is a case where the step's own gate already
    // says no and this gate used to say yes — which is how a reviewer ended up seeing
    // "required insurance has expired" and "passed compliance review, approve?" at once.
    const approvalBlockers: string[] = [];

    // The Insurance progress row is deliberately left 'Passed' when coverage lapses —
    // VPDW-4150 records the lapse in subStatus 'Coverage Issues' instead — so the
    // every-step-Passed check above is not evidence of live coverage. Ask the policies.
    if (this.InsuranceExpirationService.hasExpiredRequiredInsurance()) {
      approvalBlockers.push(
        'one or more required insurance policies have expired'
      );
    }

    // All four step components treat an overdue fee waiver as a hard stop; this gate did
    // not, so a vendor whose steps were passed by hand could still be approved from here.
    if (needsFeeWaiveRenewal) {
      approvalBlockers.push('the fee waiver needs to be renewed');
    }

    if (passesStatusAndPaymentGates && approvalBlockers.length > 0) {
      // Nothing but these blockers stands between this vendor and the approve prompt, so
      // say so. On the four routes where the insurance step is not mounted, nothing else
      // would explain why the prompt no longer appears.
      if (!this.hasWarnedApprovalBlocked) {
        this.hasWarnedApprovalBlocked = true;
        this.ToastService.addErrorToast(
          'Approval Not Offered',
          `This vendor cannot be approved yet: ${approvalBlockers.join(' and ')}.`
        );
      }

      return;
    }

    const isApprovable =
      passesStatusAndPaymentGates && approvalBlockers.length === 0;

    if (!isApprovable) {
      return;
    }

    if ((resignRequired ?? null) === null) {
      this.promptToApproveVendor(id, CompanyId, VendorId);
      return;
    }

    this.SignedDocumentService.getSignedDocuments(
      this.UtilityService.stringifyObjectsInQueryObject({
        CompanyId,
        VendorId,
        name: JSON.stringify(['Vendor Agreement', 'Vendor Agreement & W9']),
        isArchived: false,
        attributes: JSON.stringify(['id', 'name', 'signedDate'])
      })
    ).subscribe({
      next: (signedDocuments: SignedDocument[]) => {
        const hasSignedVsa = (signedDocuments ?? []).some(
          (doc) => doc.signedDate
        );
        if (hasSignedVsa) {
          this.promptToApproveVendor(id, CompanyId, VendorId);
        }
      },
      error: () => {
        if (!this.hasWarnedApprovalBlocked) {
          this.hasWarnedApprovalBlocked = true;
          this.ToastService.addErrorToast(
            'Error',
            'Could not verify the Vendor Agreement status. Try again.'
          );
        }
      }
    });
  }

  /**
   * A Compliance Waiver file is the document. The Insurance card already saved
   * Passed with Waived Findings is the record that a reviewer saw it. Both
   * have to be true, or approval clears the sub-status.
   */
  private viveSubStatusOnApproval(): 'Waived Findings' | null {
    const insurance = this.ComplianceProgressMap.get('Insurance');
    const hasWaiverFile = (this.CompanyVendor?.CompanyVendorFiles ?? []).some(
      (file) => file.docType === 'Compliance Waiver'
    );

    if (
      hasWaiverFile &&
      insurance?.status === 'Passed' &&
      insurance.subStatus === 'Waived Findings'
    ) {
      return 'Waived Findings';
    }

    return null;
  }

  private promptToApproveVendor(
    id: number | undefined,
    CompanyId: number | null | undefined,
    VendorId: number | null | undefined
  ) {
    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { size: 'md', backdrop: 'static' }
    );
    Object.assign(componentInstance, {
      displayText:
        'The vendor has passed compliance review. Do you want to approve them?'
    });

    from(result).subscribe({
      next: (response) => {
        if (response) {
          const CompanyVendorToSave: CompanyVendor = {
            id,
            CompanyId,
            VendorId,
            viveStatus: 'Approved',
            // Clear a stale sub-status (e.g. 'Re-sign Required'). The exception
            // matches the auto-approve cron: a Compliance Waiver file plus an
            // Insurance card already saved Passed with Waived Findings, which is
            // the record that a reviewer saw the document.
            viveSubStatus: this.viveSubStatusOnApproval(),
            // Nothing else clears this for a hand-approved relationship.
            resignRequired: null
          };

          from(
            companyVendorsService.updateOneById({
              pathParams: { companyVendorId: this.CompanyVendor!.id! },
              data: {
                viveStatus: 'Approved',
                viveSubStatus: this.viveSubStatusOnApproval(),
                resignRequired: null
              }
            })
          ).subscribe({
            next: () => {
              Object.assign(this.CompanyVendor!, CompanyVendorToSave);
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully approved Vendor.'
              );
              this.cdr.markForCheck();
            }
          });
        }
      }
    });
  }

  // Returns a boolean value to determine if the current VIVE Admin user can set the
  // compliance status.
  AllowedSetComplianceStatusUserRoles() {
    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (!role) {
      throw new Error('Missing user permission role.');
    }

    const UserRoleSet: Set<UserAccountRoles> = new Set([
      'ADMIN_ADMIN',
      'ADMIN_INSURANCE_TEAM',
      'ADMIN_PROJECT_MANAGEMENT_TEAM',
      'ADMIN_FRAUD_TEAM'
    ]);

    return UserRoleSet.has(role);
  }

  downloadInsuranceRequirements() {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (!this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client information.',
        { returnError: true }
      );
    }

    const { legalName } = this.Company;

    this.FileService.getCompanyVendorSampleCOI(
      this.CompanyId!,
      this.VendorId!
    ).subscribe({
      next: (SampleCertificate) => {
        const { data } = SampleCertificate;
        saveAs(
          new Blob([new Uint8Array(data)]),
          `${legalName}-SampleCertificateOfInsurance.pdf`
        );
      }
    });
  }

  setReviewStatus() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save review status. Missing Client/Vendor info.',
        { returnError: true }
      );
    }

    const ComplianceStatusModal = this.ModalService.open(
      ComplianceStatusModalComponent,
      { backdrop: 'static', modalDialogClass: 'compliance-status-modal' }
    );
    const { componentInstance } = ComplianceStatusModal;
    Object.assign(componentInstance, {
      CompanyVendor: this.CompanyVendor,
      CompanyId: this.CompanyId,
      VendorId: this.VendorId
    });
    const observable = from(ComplianceStatusModal.result) as Observable<
      [Note, CompanyVendor]
    >;
    observable.subscribe({
      next: ([Note, CompanyVendor]) => {
        this.CompanyVendor = CompanyVendor;
        this.cdr.markForCheck();
      }
    });
  }

  markVsaAsSigned() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot mark VSA as signed. Missing Client/Vendor info.',
        { returnError: true }
      );
    }

    const { CompanyId, VendorId } = this.CompanyVendor;

    if (!CompanyId || !VendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot mark VSA as signed. Missing Client/Vendor info.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      MarkVsaSignedModalComponent,
      { size: 'md', backdrop: 'static' }
    );

    Object.assign(componentInstance, { CompanyVendor: this.CompanyVendor });

    from(result).subscribe({
      next: (reason: VsaBypassReason) => {
        // This handler reports the failure itself, so the interceptor stays quiet.
        from(
          signedDocumentsService.markVsaAsSigned({
            companyId: CompanyId,
            vendorId: VendorId,
            data: { reason },
            options: {
              context: { ManuallyHandleError: true },
              requestTimeout: LOAD_BALANCER_TIMEOUT_MS
            }
          })
        ).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success!',
              reason === 'CLIENT_WAIVER'
                ? 'Marked the Vendor Service Agreement as signed using the uploaded client waiver.'
                : 'Marked the Vendor Service Agreement as signed.'
            );
          },
          error: (error: unknown) => {
            // The API's own wording is the body, kept as the error's cause.
            const { message } =
              error instanceof ViveApiError
                ? ((error.cause ?? {}) as { message?: unknown })
                : {};
            this.ToastService.addErrorToast(
              'Error',
              typeof message === 'string' && message.trim()
                ? message
                : 'Failed to mark VSA as signed.'
            );
          }
        });
      }
    });
  }

  toggleAutoLink() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot set auto link status. Missing Client/Vendor info.',
        { returnError: true }
      );
    }

    const { allowEinAutoLink } = this.CompanyVendor;

    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing client id.', {
        returnError: true
      });
    }

    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing vendor id.', {
        returnError: true
      });
    }

    this.ApVendorUtilityService.handleApAutolinkDisableModal(
      !!allowEinAutoLink,
      { CompanyId: this.CompanyId, VendorId: this.VendorId }
    ).subscribe({
      next: () => {
        const { id: CompanyVendorId } = this.CompanyVendor || {};

        from(
          companyVendorsService.updateOneById({
            pathParams: { companyVendorId: CompanyVendorId! },
            data: {
              allowEinAutoLink: !this.CompanyVendor!.allowEinAutoLink
            }
          })
        ).subscribe({
          next: (CompanyVendor) => {
            Object.assign(this.CompanyVendor!, CompanyVendor);
            this.ToastService.addSuccessToast(
              'Success!',
              `Successfully ${this.CompanyVendor!.allowEinAutoLink ? 'Enabled' : 'Disabled'} EIN Autolink.`
            );
            this.cdr.markForCheck();
          }
        });
      },
      error: () => {
        // Do nothing. Modal cancelled.
      }
    });
  }

  archiveClientVendor() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot archive Client/Vendor relationship. Missing Client/Vendor info.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      ConfirmArchiveVendorModalComponent,
      { backdrop: 'static', size: 'lg' }
    );
    Object.assign(componentInstance, { modelType: 'companyVendor' });

    from(result).subscribe({
      next: (note) => {
        if (note) {
          this.CompanyVendorService.saveCompanyVendor(
            Object.assign(this.CompanyVendor!, {
              isArchived: true,
              Notes: [note]
            })
          ).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success!',
                'Successfully archived client vendor.'
              );
              this.router.navigate(['admin', 'run-vive', { viveStatus: '' }]);
            }
          });
        }
      }
    });
  }

  goToNextStep() {
    if (
      this.CurrentComplianceStep !== 'Final Review' &&
      this.CurrentComplianceStep
    ) {
      const nextStep =
        this.ComplianceStepToNextStep[this.CurrentComplianceStep]();
      this.CurrentComplianceStep = nextStep;
      const nextRoute =
        this.ComplianceStepToRouteMap[this.CurrentComplianceStep];
      this.router.navigate([
        'admin',
        'run-vive',
        'client-vendor',
        this.CompanyVendorId,
        'client',
        this.CompanyId,
        'vendor',
        this.VendorId,
        'compliance-type',
        this.CompanyComplianceTypeId,
        'action',
        this.action,
        nextRoute
      ]);
    }
  }

  onVendorNameClick() {
    if (!this.Vendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot navigate to Vendor profile. Error retrieving vendor info.',
        { returnError: true }
      );
    }

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['admin', 'vendor', this.Vendor.id, 'info'])
    );
    window.open(url, '_blank');
  }

  onDocumentsNotesClick() {
    this.CurrentComplianceStep = undefined;
    this.router.navigate(['documents-notes'], { relativeTo: this.route });
  }

  openVendorSiteAssignments() {
    this.CurrentComplianceStep = undefined;
    this.router.navigate(['sites'], { relativeTo: this.route });
  }

  copyRegistrationURL() {
    const domainName =
      window.location.hostname === 'localhost'
        ? `${window.location.hostname}:4200`
        : window.location.hostname;
    const { clipboard } = navigator;
    from(
      clipboard.writeText(
        `${domainName}/vendor-registration/company/${this.CompanyId}/vendor/${this.VendorId}/registration/business-info/business-info`
      )
    ).subscribe({
      next: () => {
        this.ToastService.addSuccessToast(
          'Success!',
          'Successfully copied registration URL to clipboard!'
        );
      }
    });
  }

  createTicket() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot create ticket. Missing CompanyVendor info.',
        { returnError: true }
      );
    }

    const { Vendor } = this.CompanyVendor;

    if (!Vendor || (Vendor && !Vendor.ComplianceContact)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot create ticket. Missing vendor contact info.',
        { returnError: true }
      );
    }

    const CreateTicketModal = this.ModalService.open(
      CreateTicketModalComponent,
      { backdrop: 'static', modalDialogClass: 'create-ticket-modal' }
    );
    const { componentInstance } = CreateTicketModal;
    Object.assign(componentInstance, {
      CompanyVendor: this.CompanyVendor,
      CompanyId: this.CompanyId,
      VendorId: this.VendorId
    });
    const observable = from(CreateTicketModal.result) as Observable<string>;
    observable.subscribe({
      next: (url) => {
        this.ToastService.addSuccessToast(
          'Success!',
          'Successfully created Zendesk Ticket.'
        );
        window.open(url, '_blank');
      }
    });
  }

  selectComplianceType() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot select compliance type. Missing CompanyVendor info.',
        { returnError: true }
      );
    }

    const { CompanyComplianceTypeId, CompanyId } = this.CompanyVendor;

    if (!CompanyComplianceTypeId || !CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot select compliance type. Missing CompanyVendor info.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      SelectComplianceProgramModalComponent,
      { centered: true, size: 'md' }
    );

    Object.assign(componentInstance, { CompanyComplianceTypeId, CompanyId });

    from(result).subscribe({
      next: (UpdatedCompanyComplianceTypeId) => {
        if (UpdatedCompanyComplianceTypeId) {
          from(
            companyVendorsService.updateOneById({
              pathParams: { companyVendorId: this.CompanyVendor!.id! },
              data: {
                CompanyComplianceTypeId: UpdatedCompanyComplianceTypeId
              }
            })
          ).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully updated company compliance type.'
              );
              this.loadCompanyVendor().subscribe({
                next: () => {
                  this.cdr.markForCheck();
                }
              });
            }
          });
        }
      }
    });
  }

  convertVendorToClientRegistered() {
    if (!this.VendorId || !this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing vendor or company information.',
        { returnError: true }
      );
    }

    const { closed, componentInstance } = this.ModalService.open(
      ConfirmModalComponent,
      {
        centered: true,
        size: 'lg',
        backdrop: 'static'
      }
    );

    Object.assign(componentInstance, {
      displayText: `Are you sure you want to convert this vendor?`
    });

    closed.subscribe((confirm) => {
      if (confirm) {
        this.ClientRegisteredVendorService.attemptToConvertViveVendorToClientRegisteredVendor(
          this.Vendor!,
          {
            VendorId: this.VendorId,
            CompanyId: this.CompanyId
          }
        ).subscribe({
          next: (ClientRegisteredVendor) => {
            from(
              companyVendorsService.findOneByCompanyAndClientRegisteredVendor({
                pathParams: {
                  companyId: this.CompanyId!,
                  clientRegisteredVendorId: ClientRegisteredVendor.id!
                }
              })
            ).subscribe({
              next: (CompanyVendor) => {
                const { id: CompanyVendorId } = CompanyVendor;

                this.router.navigateByUrl(
                  `/admin/run-vive/client-vendor/${CompanyVendorId}/client/${this.CompanyId}/client-registered-vendor/${ClientRegisteredVendor.id}/action/edit/overview`
                );
              }
            });
          }
        });
      }
    });
  }

  convertVendorToComplianceExempt() {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing client ID.', {
        returnError: true
      });
    }

    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing vendor ID.', {
        returnError: true
      });
    }

    const { componentInstance, result } = this.ModalService.open(
      ChooseExemptVendorTypeModalComponent,
      { backdrop: 'static', centered: true, size: 'md' }
    );
    Object.assign(componentInstance, { CompanyId: this.CompanyId });

    from(result).subscribe({
      next: (ExemptVendorTypeId) => {
        if (ExemptVendorTypeId) {
          this.VendorService.convertVendorToComplianceExempt({
            CompanyId: this.CompanyId,
            VendorId: this.VendorId,
            ExemptVendorTypeId
          }).subscribe({
            next: (ExemptVendor: ExemptVendor) => {
              const { id: ExemptVendorId } = ExemptVendor;

              from(
                companyVendorsService.findOneByCompanyAndExemptVendor({
                  pathParams: {
                    companyId: this.CompanyId!,
                    exemptVendorId: ExemptVendorId!
                  }
                })
              ).subscribe({
                next: (CompanyVendor) => {
                  const { id: CompanyVendorId } = CompanyVendor;

                  this.router.navigate([
                    'admin',
                    'run-vive',
                    'client-vendor',
                    CompanyVendorId,
                    'client',
                    this.CompanyId,
                    'exempt-vendor',
                    ExemptVendorId,
                    'action',
                    'edit',
                    'overview'
                  ]);
                }
              });
            }
          });
        }
      }
    });
  }

  // A component can demand a compliance progress is reloaded.
  loadComplianceProgress(type: ComplianceSteps) {
    this.ViveProgressService.getViveProgress({
      CompanyVendorId: this.CompanyVendorId,
      type
    }).subscribe({
      next: (ComplianceProgress) => {
        this.handleUpdatedComplianceProgress(ComplianceProgress);
        this.cdr.markForCheck();
      }
    });
  }

  loadCompanyVendor() {
    // VPDW-4205: the expired-insurance check needs these before the CompanyVendor that
    // normally supplies them has loaded. They come off the same route segment as
    // CompanyVendorId, and are validated here rather than coerced — a NaN would leave the
    // approve gate answering "no expired insurance" for every vendor.
    const paramMap = this.route.snapshot.paramMap;
    const RouteCompanyId = Number(paramMap.get('CompanyId'));
    const RouteVendorId = Number(paramMap.get('VendorId'));

    if (!RouteCompanyId || !RouteVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor Id.',
        { returnError: true }
      );
    }

    return this.CompanyVendorService.getCompanyVendorCount({
      id: this.CompanyVendorId,
      isArchived: true
    }).pipe(
      concatMap((countOfArchivedClientVendorRelationships) => {
        const CompanyVendorQuery = {
          id: this.CompanyVendorId,
          aliasedAttributes: JSON.stringify([
            'renewalDate',
            // VPDW-4240: the vendor-level information-renewal due date, shown
            // beside the payment one on the compliance info card so a reviewer
            // can tell which renewal chain suspended the relationship.
            'infoRenewalDueDate',
            'hasMultipleClients',
            'vendorRiskLevel',
            'lastPaymentDate'
          ]),
          Vendor: JSON.stringify({
            attributes: [
              'id',
              'legalName',
              'dba',
              'website',
              'isOnSite',
              'ein',
              'bsn',
              'isPubliclyTraded',
              // VPDW-4240: the LAST COMPLETED information renewal. Null until
              // the vendor completes their first one, which is how the card
              // tells a real renewal anniversary apart from a due date derived
              // from their signup date.
              'renewalDate'
            ],
            ComplianceContact: {},
            VendorTradeState: {
              SubNationalDivision: {}
            },
            StreetAddress: {
              options: { required: false },
              attributes: ['id', 'address1', 'address2', 'city', 'state', 'zip']
            },
            MailingAddress: {
              options: { required: false },
              attributes: ['id', 'address1', 'address2', 'city', 'state', 'zip']
            },
            RemitToAddress: {
              options: { required: false },
              attributes: ['id', 'address1', 'address2', 'city', 'state', 'zip']
            }
          }),
          Company: JSON.stringify({
            attributes: [
              'id',
              'legalName',
              'fullServiceOnsite',
              'fullServiceOffsite',
              'allowAutomatedBusinessVerificationClientRegisteredVendors',
              'allowClientRegisteredVendors'
            ],
            CompanyOperationState: {
              SubNationalDivision: {}
            }
          }),
          CompanyVendorFile: JSON.stringify({
            attributes: ['id', 'docType'],
            docType: { $in: ['Important Note', 'Compliance Waiver'] }
          }),
          ViveProgress: JSON.stringify({
            attributes: JSON.stringify([
              'id',
              'CompanyVendorId',
              'CompanyId',
              'VendorId',
              'type',
              'status',
              'subStatus',
              'statusDate',
              'ticketUrl'
            ]),
            VendorTradeState: {
              SubNationalDivision: {}
            }
          }),
          CompanyComplianceType: JSON.stringify({}),
          WSIB: JSON.stringify({}),
          WCB: JSON.stringify({}),
          RecentPendingOrPaidCompanyVendorPayment: JSON.stringify({
            attributes: ['id', 'expStatus']
          })
        };

        if (countOfArchivedClientVendorRelationships > 0) {
          Object.assign(CompanyVendorQuery, { isArchived: true });
        }

        return forkJoin([
          this.CompanyVendorService.getCompanyVendor(CompanyVendorQuery),
          // VPDW-4205: prime the expired-insurance answer before `this.CompanyVendor` is
          // assigned below. The <router-outlet> is gated on `CompanyVendor`, so completing
          // this first guarantees the approve gate can answer before any step component
          // exists — including on the four routes where the insurance step, which owns the
          // only other copy of this question, never mounts at all.
          this.InsuranceExpirationService.load(
            this.CompanyVendorId,
            RouteCompanyId,
            RouteVendorId
          )
        ]).pipe(
          concatMap(([CompanyVendor]) => {
            if (!CompanyVendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client/Vendor information.',
                { returnError: true }
              );
            }

            const {
              CompanyId,
              VendorId,
              CompanyComplianceTypeId,
              Company,
              Vendor,
              ViveProgresses,
              CompanyVendorFiles,
              viveStatus,
              lastPaymentDate
            } = CompanyVendor;

            if (!CompanyId) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client Id.',
                {
                  returnError: true
                }
              );
            }

            if (!VendorId) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Vendor Id.',
                {
                  returnError: true
                }
              );
            }

            if (!CompanyComplianceTypeId) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Compliance Type Id.',
                { returnError: true }
              );
            }

            if (!Company) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client information.',
                { returnError: true }
              );
            }

            if (!Vendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Vendor information.',
                { returnError: true }
              );
            }

            if (
              CompanyVendorFiles?.some(
                (file) => file.docType === 'Important Note'
              )
            ) {
              this.ToastService.addErrorToast(
                'Info',
                'Important Note attached to profile.'
              );
            }

            const {
              fullServiceOnsite,
              fullServiceOffsite,
              CompanyOperationStates,
              allowClientRegisteredVendors
            } = Company;
            const { isOnSite, VendorTradeStates, isPubliclyTraded } = Vendor;

            if (typeof fullServiceOnsite !== 'boolean') {
              throw this.ToastService.addErrorToast(
                'Error',
                'There was an error retrieving Client on site information.',
                { returnError: true }
              );
            }

            if (typeof fullServiceOffsite !== 'boolean') {
              throw this.ToastService.addErrorToast(
                'Error',
                'There was an error retrieving Client off site information.',
                { returnError: true }
              );
            }

            if (typeof isOnSite !== 'boolean') {
              throw this.ToastService.addErrorToast(
                'Error',
                'There was an error retrieving Vendor on site/off site information.',
                { returnError: true }
              );
            }

            if (!CompanyOperationStates) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client operation states.',
                { returnError: true }
              );
            }

            if (!VendorTradeStates) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Vendoror trade states.',
                { returnError: true }
              );
            }

            if (!viveStatus) {
              throw this.ToastService.addErrorToast(
                'Error',
                'There was an error retrieving Vendor VIVE status.',
                { returnError: true }
              );
            }

            this.canConvertVendorToClientRegisteredVendor =
              !!allowClientRegisteredVendors &&
              this.acceptableVendorConversionStatuses.has(viveStatus) &&
              !lastPaymentDate;

            const VendorTradeStateSubNationalDivisionSet = new Set<State>(
              VendorTradeStates.map((vts) => {
                const { SubNationalDivision } = vts;

                if (!SubNationalDivision) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Vendor trade state is missing sub national division information.',
                    { returnError: true }
                  );
                }

                const { abbreviation } = SubNationalDivision;

                if (!abbreviation) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Sub national division is missing state information.'
                  );
                }

                return abbreviation;
              })
            );

            CompanyOperationStates.forEach((vts) => {
              const { SubNationalDivision } = vts;

              if (!SubNationalDivision) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Vendor trade state is missing sub national division information.',
                  { returnError: true }
                );
              }

              const { abbreviation } = SubNationalDivision;

              if (!abbreviation) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Sub national division is missing state information.'
                );
              }

              if (VendorTradeStateSubNationalDivisionSet.has(abbreviation)) {
                this.SharedOperationStates.add(abbreviation);
              }
            });

            // Same rule as the compliance cron. An SSN waives the finding
            // inside the step, and Canada does not drop it. A publicly traded
            // company has no Owner Info review.
            this.clientPackageType =
              ((fullServiceOnsite && isOnSite) ||
                (fullServiceOffsite && !isOnSite)) &&
              !isPubliclyTraded
                ? 'FULL'
                : 'BASIC';

            if (this.clientPackageType === 'FULL') {
              this.ComplianceProgressMap.set('Owner Info', null);
            }

            if (Array.isArray(ViveProgresses)) {
              ViveProgresses.forEach((ViveProgressRecord) => {
                const { type } = ViveProgressRecord;

                if (!type) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Compliance Progress is missing type information.',
                    { returnError: true }
                  );
                }

                this.ComplianceProgressMap.set(
                  type,
                  new ViveProgress(ViveProgressRecord)
                );
              });
            }

            Object.assign(this, {
              CompanyVendor,
              Vendor,
              Company,
              CompanyComplianceTypeId,
              CompanyId,
              VendorId
            });

            this.askToApproveVendor();
            this.cdr.markForCheck();
            return of();
          })
        );
      })
    );
  }

  ngOnInit(): void {
    // VPDW-4199: this component owns the only view of every step's status. Share it so
    // the routed step components can scope their Auto-Approve prompt to the step they
    // actually approve instead of implying the whole review is clean. The map is handed
    // over by reference and is only ever mutated in place (never reassigned), so this
    // one call stays current for the rest of the review.
    this.ComplianceStepStatusService.setStepStatuses(
      this.ComplianceProgressMap
    );

    const paramMap = this.route.snapshot.paramMap;

    const currentRoute = this.router.url.split('/').pop() as ComplianceRoute;

    const CurrentComplianceStep = this.ComplianceRouteToStepMap[currentRoute];
    if (CurrentComplianceStep) {
      Object.assign(this, { CurrentComplianceStep });
    }

    const CompanyVendorId = Number(paramMap.get('CompanyVendorId'));
    const action = paramMap.get('action');

    if (!CompanyVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor Id.',
        { returnError: true }
      );
    }

    Object.assign(this, { CompanyVendorId, action });

    this.loadCompanyVendor().subscribe({
      next: () => {
        this.cdr.markForCheck();
      }
    });
  }
}
